import clsx from 'clsx';
import { useResolvePatientUserAddQuestions } from '../hooks/useResolvePatientUserAddQuestions';
import { PatientRecord } from '../types';
import { Tag } from 'antd';
import {
  clinicActionEditPatientDetails,
  clinicActionResendInvitation
} from 'lib/api/firebase/apis/apis';
import { stringOrNull } from 'lib/utils/string/normalise';
import { TagEditDetails } from 'features/clinician/clinic-user-management/components/tag-edit-details';
import {
  ActionTagOnClick,
  ActionTagRenderProps
} from 'components/action-tag/action-tag-component';
import { TableRow } from '../../user-table-types';

export const ACTION_SENDSMSINVITE_ONCLICK: (
  clinicId: string
) => ActionTagOnClick<TableRow<PatientRecord>, any> =
  (clinicId: string) =>
  async (
    record,
    extraData:
      | { type: 'new_number'; phoneNumber: string | null }
      | { type: 'existing_number' }
  ) => {
    /**
     * Throw an error if the ID is not available
     */
    if (!record.raw.id) {
      throw new Error('ID is undefined. Please try reloading the page');
    }

    /**
     * To invoke the sms-invitation action we need to update the user record to have a phone number.
     *
     * This requires invoking editPatientDetails with all the required properties.
     * This implicitly creates a requirement that clinicianId must be set before SMS invitations can be sent
     */
    if (record.raw.assignedClinicians.length === 0) {
      throw new Error('Please assign a clinician to enable the SMS invite');
    }

    /**
     * A new number was provided
     * Update the user record
     */
    if (extraData.type === 'new_number') {
      await clinicActionEditPatientDetails({
        patientId: record.raw.id,
        firstName: record.firstName,
        lastName: record.lastName,
        email: record.email,
        /**
         * Convert empty strings to null
         */
        phoneNumber: stringOrNull(extraData.phoneNumber),
        clinicianId: record.raw.assignedClinicians[0].id,
        clinicId: clinicId,
        assessmentType: record.raw.nextAssessmentType
      });
    }

    // Resend the invitation link
    return clinicActionResendInvitation({
      invitationId: record.raw.id,
      resendType: 'sms'
    });
  };

export function ActionSendSMSInvite(
  props: { clinicId: string } & ActionTagRenderProps<TableRow<PatientRecord>>
) {
  const { clinicId, ..._props } = props;
  const patientUserAddQuestions = useResolvePatientUserAddQuestions(clinicId);

  /**
   * If the phone number is already set then skip the edit-user modal
   */
  if (_props.record.raw.phoneNumber) {
    return (
      <Tag
        {..._props}
        {..._props.tagStyles}
        className={clsx('clickable', _props.disabled && 'disabled')}
        onClick={() => {
          _props.onClick(_props.record, {
            type: 'existing_number'
          });
        }}
      >
        {_props.name}
      </Tag>
    );
  }

  /**
   * The phoneNumber is not set, render a tag to set the property
   */
  return (
    <TagEditDetails
      {..._props}
      userAddQuestions={patientUserAddQuestions.filter(
        _ => _.name === 'phoneNumber'
      )}
      initialValues={{
        phoneNumber: _props.record.raw.phoneNumber
      }}
      onClick={(record, extra) => {
        _props.onClick(record, {
          type: 'new_number',
          ...extra
        });
      }}
    />
  );
}
